/**
 * community-intelligence/reputation.ts — contributor reputation + evidence weight.
 *
 * Pure functions: reputation is DERIVED from raw signals every time (never a
 * stored number that drifts), and each contribution's aggregation weight is
 * tier weight × reputation multiplier. Verified members carry the most weight;
 * higher reputation increases recommendation weight, exactly per spec.
 */

import type { ContributorProfile, ContributorTier, ReputationSignals } from './models';

/** Base evidence weight per provenance tier. */
export const TIER_WEIGHTS: Record<ContributorTier, number> = {
  verified: 1.0,
  external: 0.6,
  anonymous: 0.4,
};

export const EMPTY_SIGNALS: ReputationSignals = {
  helpfulVotes: 0,
  verifiedCheckIns: 0,
  missionsCompleted: 0,
  travelExperience: 0,
  contributionQuality: 0,
  communityTrust: 0,
  reportAccuracy: 1, // innocent until inaccurate
};

/** Saturating count → 0–1 (K = half point). */
function saturate(count: number, K: number): number {
  return count / (count + K);
}

/**
 * Reputation 0–100 from the spec's seven signals.
 * Count-based signals saturate; ratio signals apply directly.
 */
export function computeReputation(signals: ReputationSignals): number {
  const score =
    saturate(signals.helpfulVotes, 20) * 25 +       // Helpful Votes
    saturate(signals.verifiedCheckIns, 15) * 15 +   // Verified Check-ins
    saturate(signals.missionsCompleted, 10) * 10 +  // Mission Completion
    saturate(signals.travelExperience, 8) * 10 +    // Travel Experience
    signals.contributionQuality * 15 +              // Contribution Quality
    signals.communityTrust * 10 +                   // Community Trust
    signals.reportAccuracy * 15;                    // Report Accuracy
  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Weight of one contributor's evidence in aggregation:
 * tier base × reputation multiplier (0.5× at rep 0 → 1.5× at rep 100).
 */
export function contributionWeight(tier: ContributorTier, reputation: number): number {
  const repMultiplier = 0.5 + reputation / 100;
  return TIER_WEIGHTS[tier] * repMultiplier;
}

export function buildProfile(
  id: string,
  tier: ContributorTier,
  displayName: string,
  signals: ReputationSignals,
  joinedAt: number,
): ContributorProfile {
  return {
    id,
    tier,
    displayName,
    signals: { ...signals },
    reputation: computeReputation(signals),
    joinedAt,
  };
}
